import {UIManager} from './UIManager'
import {notificationController, uiGameManager} from '../../../shared/scripts/EntryShared'
import {Scene} from './UISceneTypes'
import {UIRoomController} from './room/UIRoomController'
import {UIUtilsEjsVariables} from '../../../shared/scripts/UIUtilsEjsVariables'
import {UIOnlineFriendController} from '../../../shared/scripts/ui_friends/UIOnlineFriendController'
import {ClientUserState} from '../../../shared/scripts/ClientUserState'
import {PlayerColor} from '@colonist/game-states'

export class UISceneController {
    get scene(): Scene {
        return this._scene
    }

    set scene(value: Scene) {
        this._scene = value
    }

    private _scene: Scene
    private readonly uiManager: UIManager

    constructor(uiManager: UIManager) {
        this.scene = Scene.LandingPage
        this.uiManager = uiManager

        ClientUserState.registerChangeListener(() => {
            if(this.shouldShowMainSceneOnUserInit()) {
                this.loadScene(Scene.MainPage)
            }
        })
    }

    private shouldShowMainSceneOnUserInit(): boolean {
        // if the conditions for showing the main page change, update `first-scene-detection.ejs`
        const userState = ClientUserState.userState
        if(this.scene != Scene.LandingPage) return false
        if(userState == undefined) return false
        if(UIUtilsEjsVariables.isUserOnDiscord && userState.hasZeroCompletedGames()) return false
        if(UIUtilsEjsVariables.isUserOnMobileApp) return true
        if(userState.isLoggedIn) return true
        if(!userState.hasZeroCompletedGames()) return true

        return false
    }

    loadScene(scene: Scene, data?: Record<string, unknown>) {
        this.scene = scene
        this.hideAllScenes()

        switch(scene) {
            case Scene.Lobby:
                this.loadLobbyScene()
                UISceneController.setWindowHistoryState(scene)
                break
            case Scene.Room:
                this.loadRoomScene()
                break
            case Scene.Game:
                this.loadGameScene()
                break
            case Scene.LandingPage:
                this.loadLandingPageScene()
                UISceneController.setWindowHistoryState(scene)
                break
            case Scene.MainPage:
                this.loadMainPageScene()
                UISceneController.setWindowHistoryState(scene)
                break
            case Scene.DiscordLeaderboard:
            case Scene.DiscordStore:
            case Scene.DiscordAbout:
            case Scene.DiscordCareers:
            case Scene.DiscordCommunity:
            case Scene.DiscordFAQ:
            case Scene.DiscordPrivacyPolicy:
            case Scene.DiscordPatchNotes:
            case Scene.DiscordValues:
            case Scene.DiscordTerms:
            case Scene.DiscordPressKit:
            case Scene.DiscordRefundPolicy:
                this.loadDiscordScene(scene, data)
                break
            case Scene.DiscordProfile:
                void this.loadDiscordProfileScene(data)
                break
            case Scene.DiscordReplay:
                this.loadDiscordReplayScene(data)
                break
            case Scene.DiscordGifting:
                this.loadDiscordGiftScene(data)
                break
        }
    }

    hideAllScenes() {
        this.uiManager.header.hide()
        this.uiManager.footer.hide()

        this.uiManager.landingPage.hide()
        this.uiManager.lobby.hide()
        this.uiManager.room.hide()
        this.uiManager.mainPage.hide()
        this.uiManager.homepageSceneContainer.style.display = 'none'
        this.uiManager.discordSceneController?.hideAllScenes()
    }

    private static setWindowHistoryState(scene: Scene) {
        if(UISceneController.shouldSetWindowHistoryState(scene)) {
            window.history.replaceState({pageScene: scene}, document.title, window.location.href)
        }
    }

    private static shouldSetWindowHistoryState(scene: Scene): boolean {
        if(window.history.state == null) return true
        if(window.history.state == '') return true
        if(window.history.state.pageScene != scene) return true
        return false
    }

    private loadDiscordScene(scene: Scene, data?: { hash?: string, value?: string }) {
        if(!UIUtilsEjsVariables.isUserOnDiscord) throw new Error('Not on Discord Integration Server')
        this.uiManager.header.show()
        if(!UIUtilsEjsVariables.isUserOnDiscordMobile) this.uiManager.footer.show()
        this.uiManager.discordSceneController?.showScene(scene, data?.hash, data?.value)
    }

    private loadLobbyScene() {
        this.uiManager.header.activateLobbyButton()
        this.uiManager.header.show()
        this.uiManager.footer.show()
        this.uiManager.homepageSceneContainer.style.display = 'flex'
        this.uiManager.lobby.show()
        UIOnlineFriendController.userOnlineAndIdle()
    }

    private loadRoomScene() {
        const showHeader = UIRoomController.showHeaderInRoom()
        if(showHeader) {
            this.uiManager.header.activateLobbyButton()
            this.uiManager.header.show()
        }
        if(!UIUtilsEjsVariables.isUserOnDiscordMobile) this.uiManager.footer.show()
        this.uiManager.room.show()
        notificationController.removeDisconnectBanners()
        UIOnlineFriendController.userEnteredRoom()
    }

    private loadGameScene() {
        this.uiManager.matchmakingController.removePopups()
        this.uiManager.adController.removeLobbyAds()
        notificationController.removeDisconnectBanners()
        this.uiManager.clearDOM()
        uiGameManager.canvasController.buildGame()
        UIOnlineFriendController.userEnteredGame()
    }

    private loadLandingPageScene() {
        this.uiManager.header.activateHomeButton()
        this.uiManager.header.show()
        this.uiManager.footer.show()
        this.uiManager.landingPage.show()
    }

    private loadMainPageScene() {
        // if the elements that are shown here change, update `first-scene-detection.ejs`
        this.uiManager.header.activateHomeButton()
        this.uiManager.header.show()
        this.uiManager.footer.show()
        this.uiManager.homepageSceneContainer.style.display = 'flex'
        this.uiManager.mainPage.show()
    }

    private async loadDiscordProfileScene(data?: Record<string, unknown>) {
        if(!UIUtilsEjsVariables.isUserOnDiscord) throw new Error('Not on Discord Integration Server')
        this.uiManager.header.show()
        if(!UIUtilsEjsVariables.isUserOnDiscordMobile) this.uiManager.footer.show()

        const username = data?.username as string
        const discordSceneController = this.uiManager.discordSceneController
        if(username != discordSceneController?.discordProfileController.profileController.username) {
            await discordSceneController?.discordProfileController.updatePageContent(username)
        }
        discordSceneController?.showScene(Scene.DiscordProfile)
    }

    private loadDiscordReplayScene(data?: Record<string, unknown>) {
        if(!UIUtilsEjsVariables.isUserOnDiscord) throw new Error('Not on Discord Integration Server')
        if(data?.gameId == undefined) return
        if(data?.playerColor == undefined) return

        const gameId = data.gameId as string
        const playerColor = data.playerColor as PlayerColor

        this.uiManager.discordSceneController?.discordReplayController.loadReplay(gameId, playerColor)
    }

    private loadDiscordGiftScene(data?: Record<string, unknown>) {
        if(!UIUtilsEjsVariables.isUserOnDiscord) throw new Error('Not on Discord Integration Server')

        const discordSceneController = this.uiManager.discordSceneController
        const giftOverlayController = discordSceneController?.discordStoreController.storeController.bodyController.giftOverlayController

        const recipientUsername = data?.recipientUsername as string | undefined
        const shouldRecipientSelectionBeFirstPage = recipientUsername != undefined
        giftOverlayController?.reorderPages(shouldRecipientSelectionBeFirstPage)
        giftOverlayController?.updateRecipient(recipientUsername)
        discordSceneController?.showScene(Scene.DiscordGifting)
    }
}
